import React, { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Send, Loader2, FileSpreadsheet, Check, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const quoteSchema = z.object({
  name: z.string().min(2, 'Nom requis'),
  email: z.string().email('Courriel invalide'),
  phone: z.string().min(7, 'Téléphone requis'),
  company: z.string().optional(),
  project_type: z.enum(['rental', 'purchase', 'parts', 'software', 'service', 'other'], {
    errorMap: () => ({ message: 'Sélectionnez un type de projet' }),
  }),
  equipment_description: z.string().min(3, 'Décrivez l\u2019équipement souhaité'),
  rental_start_date: z.string().optional(),
  rental_end_date: z.string().optional(),
  budget_range: z.string().optional(),
  location: z.string().optional(),
  details: z.string().optional(),
});

const PROJECT_TYPES = [
  { value: 'rental', label: 'Location d\u2019équipement' },
  { value: 'purchase', label: 'Achat d\u2019équipement' },
  { value: 'parts', label: 'Pièces & accessoires' },
  { value: 'software', label: 'Logiciel & licences' },
  { value: 'service', label: 'Services & installation' },
  { value: 'other', label: 'Autre' },
];

const BUDGET_OPTIONS = [
  { value: '<5k', label: 'Moins de 5 000 $' },
  { value: '5-15k', label: '5 000 $ - 15 000 $' },
  { value: '15-50k', label: '15 000 $ - 50 000 $' },
  { value: '50k+', label: 'Plus de 50 000 $' },
  { value: 'discuss', label: 'À discuter' },
];

export default function Quote() {
  // Translation not used currently in this page
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(quoteSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      company: '',
      project_type: '',
      equipment_description: '',
      rental_start_date: '',
      rental_end_date: '',
      budget_range: '',
      location: '',
      details: '',
    },
  });

  const projectType = watch('project_type');
  const isRental = projectType === 'rental';

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      // Strip empty strings
      const cleanData = Object.fromEntries(
        Object.entries(data).filter(([_, v]) => v !== '' && v !== undefined)
      );
      await axios.post(`${API}/quote-requests`, cleanData);
      toast.success('Demande envoyée avec succès!');
      setSubmitted(true);
      reset();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\u2019envoi');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-white" data-testid="quote-page-success">
        <div className="container mx-auto px-4 py-24 max-w-2xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center space-y-6"
          >
            <div className="mx-auto w-20 h-20 rounded-full bg-black flex items-center justify-center">
              <CheckCircle2 className="h-10 w-10 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-black">Demande reçue!</h1>
            <p className="text-gray-600 text-lg">
              Merci pour votre demande de soumission. Notre équipe vous contactera dans les plus brefs délais (généralement dans les 24 heures ouvrables).
            </p>
            <div className="flex gap-3 justify-center pt-4">
              <Button
                onClick={() => setSubmitted(false)}
                variant="outline"
                className="border-gray-300 text-black hover:bg-gray-50"
                data-testid="quote-new-request-btn"
              >
                Faire une autre demande
              </Button>
              <Button
                onClick={() => (window.location.href = '/')}
                className="bg-black text-white hover:bg-gray-800"
              >
                Retour à l&apos;accueil
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="quote-page">
      {/* Header */}
      <section className="relative py-20 overflow-hidden bg-gray-50">
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-black rounded-lg flex items-center justify-center">
                <FileSpreadsheet className="h-6 w-6 text-white" />
              </div>
              <h1 className="text-4xl font-bold text-black">Demande de soumission</h1>
            </div>
            <p className="text-gray-600 text-lg">
              Remplissez ce formulaire et notre équipe vous fera parvenir une soumission personnalisée dans les 24 heures ouvrables.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form */}
      <section className="py-12">
        <div className="container mx-auto px-4 max-w-4xl">
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-8" data-testid="quote-form">
                {/* Identity */}
                <div>
                  <h2 className="text-lg font-semibold text-black mb-1">Vos coordonnées</h2>
                  <p className="text-sm text-gray-500 mb-4">Pour que nous puissions vous joindre</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-gray-700">Nom complet *</Label>
                      <Input
                        id="name"
                        {...register('name')}
                        className="bg-white border-gray-300 text-black"
                        data-testid="quote-name"
                      />
                      {errors.name && <p className="text-red-600 text-sm">{errors.name.message}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-gray-700">Courriel *</Label>
                      <Input
                        id="email"
                        type="email"
                        {...register('email')}
                        className="bg-white border-gray-300 text-black"
                        data-testid="quote-email"
                      />
                      {errors.email && <p className="text-red-600 text-sm">{errors.email.message}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-gray-700">Téléphone *</Label>
                      <Input
                        id="phone"
                        type="tel"
                        {...register('phone')}
                        className="bg-white border-gray-300 text-black"
                        data-testid="quote-phone"
                        placeholder="514-555-1234"
                      />
                      {errors.phone && <p className="text-red-600 text-sm">{errors.phone.message}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company" className="text-gray-700">Entreprise (facultatif)</Label>
                      <Input
                        id="company"
                        {...register('company')}
                        className="bg-white border-gray-300 text-black"
                        data-testid="quote-company"
                      />
                    </div>
                  </div>
                </div>

                {/* Project */}
                <div className="border-t border-gray-100 pt-8">
                  <h2 className="text-lg font-semibold text-black mb-1">Votre projet</h2>
                  <p className="text-sm text-gray-500 mb-4">Décrivez votre besoin</p>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-gray-700">Type de projet *</Label>
                      <Controller
                        name="project_type"
                        control={control}
                        render={({ field }) => (
                          <Select onValueChange={field.onChange} value={field.value}>
                            <SelectTrigger className="bg-white border-gray-300 text-black" data-testid="quote-project-type">
                              <SelectValue placeholder="Sélectionnez un type" />
                            </SelectTrigger>
                            <SelectContent className="bg-white border-gray-200">
                              {PROJECT_TYPES.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value} data-testid={`quote-project-type-${opt.value}`}>
                                  {opt.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                      {errors.project_type && <p className="text-red-600 text-sm">{errors.project_type.message}</p>}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="equipment_description" className="text-gray-700">Équipement / produit souhaité *</Label>
                      <Input
                        id="equipment_description"
                        {...register('equipment_description')}
                        className="bg-white border-gray-300 text-black"
                        placeholder="Ex: Écran LED extérieur 3m x 2m, panneau intérieur 55po..."
                        data-testid="quote-equipment"
                      />
                      {errors.equipment_description && (
                        <p className="text-red-600 text-sm">{errors.equipment_description.message}</p>
                      )}
                    </div>

                    {isRental && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200"
                        data-testid="quote-rental-dates"
                      >
                        <div className="space-y-2">
                          <Label htmlFor="rental_start_date" className="text-gray-700">Date de début</Label>
                          <Input
                            id="rental_start_date"
                            type="date"
                            {...register('rental_start_date')}
                            className="bg-white border-gray-300 text-black"
                            data-testid="quote-start-date"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="rental_end_date" className="text-gray-700">Date de fin</Label>
                          <Input
                            id="rental_end_date"
                            type="date"
                            {...register('rental_end_date')}
                            className="bg-white border-gray-300 text-black"
                            data-testid="quote-end-date"
                          />
                        </div>
                      </motion.div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-gray-700">Budget estimé (facultatif)</Label>
                        <Controller
                          name="budget_range"
                          control={control}
                          render={({ field }) => (
                            <Select onValueChange={field.onChange} value={field.value}>
                              <SelectTrigger className="bg-white border-gray-300 text-black" data-testid="quote-budget">
                                <SelectValue placeholder="Sélectionnez" />
                              </SelectTrigger>
                              <SelectContent className="bg-white border-gray-200">
                                {BUDGET_OPTIONS.map((opt) => (
                                  <SelectItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="location" className="text-gray-700">Lieu / Ville (facultatif)</Label>
                        <Input
                          id="location"
                          {...register('location')}
                          className="bg-white border-gray-300 text-black"
                          placeholder="Ex: Montréal, Québec, Laval..."
                          data-testid="quote-location"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="details" className="text-gray-700">Précisions supplémentaires (facultatif)</Label>
                      <Textarea
                        id="details"
                        {...register('details')}
                        rows={5}
                        className="bg-white border-gray-300 text-black"
                        placeholder="Décrivez votre projet, les conditions particulières, l'environnement d'installation, etc."
                        data-testid="quote-details"
                      />
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-100 pt-6 flex items-center justify-between gap-4 flex-wrap">
                  <p className="text-sm text-gray-500">
                    <Check className="h-4 w-4 inline mr-1 text-green-600" />
                    Réponse garantie dans les 24 heures ouvrables
                  </p>
                  <Button
                    type="submit"
                    disabled={loading}
                    size="lg"
                    className="bg-black text-white hover:bg-gray-800"
                    data-testid="quote-submit"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4 mr-2" />
                    )}
                    Envoyer ma demande
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}
